// src/lib/site.js
// CRA-first, Vite-friendly, canonical-safe helpers

const CRA  = (typeof process !== "undefined" && process.env) || {};
const VITE = (typeof import.meta !== "undefined" && import.meta.env) || {};
const IN_BROWSER = typeof window !== "undefined";

function firstEnv(...keys) {
  for (const k of keys) {
    const v = (CRA[k] ?? VITE[k]) ?? "";
    if (v) return String(v);
  }
  return "";
}

function stripTrailingSlash(s) {
  return s && s !== "/" ? s.replace(/\/+$/, "") : s;
}

function getOrigin() {
  const envOrigin = firstEnv("REACT_APP_SITE_ORIGIN", "VITE_SITE_ORIGIN");
  const winOrigin = IN_BROWSER ? window.location.origin : "";
  return stripTrailingSlash(envOrigin || winOrigin || "");
}

export const SITE = getOrigin();
export const ORIGIN = SITE; // back-compat for older imports

function join(origin, p = "/") {
  if (/^https?:\/\//i.test(p)) return stripTrailingSlash(p); // already absolute
  const path = p.startsWith("/") ? p : `/${p}`;
  return origin ? `${origin}${path}` : path; // relative fallback if origin missing
}

function normalizeCanonical(u) {
  // Ensures absolute URL, no fragment, no trailing slash (except root)
  try {
    const base = SITE || (IN_BROWSER ? window.location.origin : "http://localhost");
    const url = new URL(u, base);
    if (url.pathname !== "/") url.pathname = url.pathname.replace(/\/+$/, "");
    url.hash = "";
    return url.toString();
  } catch {
    return stripTrailingSlash(u);
  }
}

export const url = {
  // generic helpers
  abs:       (p = "/") => join(SITE, p),
  canonical: (p = "/") => normalizeCanonical(join(SITE, p)),

  // routes (paths only)
  cityPath:    (slug) => `/${slug}/party-decorations`,
  servicePath: (slug) => `/services/${slug}`,
  packagePath: (slug) => `/packages/${slug}`,

  // absolute URLs (handy for canonicals / JSON-LD)
  cityUrl:     (slug) => join(SITE, `/${slug}/party-decorations`),
  serviceUrl:  (slug) => join(SITE, `/services/${slug}`),
  packageUrl:  (slug) => join(SITE, `/packages/${slug}`),
};

export const WHATSAPP = {
  number: (firstEnv("REACT_APP_WHATSAPP_NUMBER", "VITE_WHATSAPP_NUMBER") || "").replace(/\s+/g, ""),
  defaultMessage: firstEnv("REACT_APP_WHATSAPP_MESSAGE", "VITE_WHATSAPP_MESSAGE") || "",
  link(text) {
    const num = this.number.replace(/^\+/, "").replace(/\D/g, "");
    const msg = encodeURIComponent(text || this.defaultMessage || "");
    return num ? `https://wa.me/${num}?text=${msg}` : "https://wa.me/";
  },
};

export const API = {
  url:  firstEnv("REACT_APP_API_URL",  "VITE_API_URL")  || "",
  base: firstEnv("REACT_APP_API_BASE", "VITE_API_BASE") || "",
};

export const SOCIAL = {
  instagram: firstEnv("REACT_APP_INSTAGRAM_URL", "VITE_INSTAGRAM_URL") || "https://www.instagram.com/party_crafters_/",
  facebook:  firstEnv("REACT_APP_FACEBOOK_URL",  "VITE_FACEBOOK_URL")  || "https://www.facebook.com/Partycrafters25",
  tiktok:    firstEnv("REACT_APP_TIKTOK_URL",    "VITE_TIKTOK_URL")    || "https://www.tiktok.com/@party_crafters_",
  sameAs() { return [this.instagram, this.facebook, this.tiktok].filter(Boolean); },
};

// Dev hint so we don't silently ship relative canonicals in prod
if (!SITE && !(IN_BROWSER && /^https?:\/\/localhost(:\d+)?$/.test(window.location.origin))) {
  // eslint-disable-next-line no-console
  console.warn('[site] SITE origin is empty; canonicals will be relative. Set REACT_APP_SITE_ORIGIN for CRA (or VITE_SITE_ORIGIN for Vite).');
}
